import { useState } from "react";
import { AdSystemT, CurrencyT, useInitiatePaymentMutation } from "../../graphql/generated/graphql";
import { getAppHost } from "../../utils/getAppHost";
import { isInIframe } from "../../utils/isInIframe";

export const roundAmount = (amount: number) => Math.ceil(amount / 10) * 10;

export const getCurrencyLimits = (currency: CurrencyT) => {
  let minimalPaymentAmount: number;
  let maximalPaymentAmount: number;

  switch (currency) {
    case CurrencyT.EurT:
      minimalPaymentAmount = 40;
      maximalPaymentAmount = 4000;
      break;
    case CurrencyT.CzkT:
    default:
      minimalPaymentAmount = 1000;
      maximalPaymentAmount = 100000;
      break;
  }

  return { minimalPaymentAmount, maximalPaymentAmount };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCypressTest = typeof (window as any).Cypress !== "undefined";

const getPaymentReturnUrl = (): string | undefined => {
  // Cypress e2e tests: use current page URL for correct redirect
  if (isCypressTest) {
    return window.location.href;
  }
  // Development: use configured app host (handles port mismatch in iframe)
  if (process.env.NODE_ENV === "development") {
    return getAppHost();
  }
  // Production: let backend use organization.shoptet_url
  return undefined;
};

type UseCreditPaymentPropsT = {
  currency: CurrencyT;
  onError?: () => void;
  onSuccess?: () => void;
  returnUrl?: string;
  startCampaignFor?: AdSystemT;
};

export const useCreditPayment = ({
  currency,
  onError,
  onSuccess,
  returnUrl,
  startCampaignFor,
}: UseCreditPaymentPropsT) => {
  const [isRedirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(false);

  const [initiatePayment, { error: mutationError, loading: loadingPayment }] = useInitiatePaymentMutation({
    onCompleted(response) {
      const urlToPay = response.initiatePayment?.payment?.urlToPay;

      if (urlToPay) {
        const link = document.createElement("a");
        link.href = urlToPay;
        if (isInIframe() && !isCypressTest) {
          link.target = "_top";
        }
        link.click();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        setRedirecting(false);
        setError(true);
        if (onError) {
          onError();
        }
      }
    },
    onError() {
      setRedirecting(false);
      setError(true);
      if (onError) {
        onError();
      }
    },
  });

  const initiatePaymentForAmount = (amount: number) => {
    setError(false);
    setRedirecting(true);
    initiatePayment({
      variables: {
        amount: roundAmount(amount),
        currency,
        returnUrl: returnUrl || getPaymentReturnUrl(),
        startCampaignFor,
      },
    });
  };

  return {
    initiatePaymentForAmount,
    isRedirecting,
    error: error || !!mutationError,
    loading: loadingPayment,
  };
};
